import {
  OFFER_INCLUSION_LABELS,
  VAT_REDUCED_BP,
  VAT_STANDARD_BP,
  formatEur,
  formatVatRate,
  priceOffer,
  type AwardedRequesterContact,
  type OfferInclusions,
  type OfferInput,
  type OfferLineItem,
  type OfferSupplierView,
  type RfqSupplierView,
} from '@caterinx/shared-types';
import { Alert, AlertDescription, AlertTitle } from '@caterinx/ui/components/ui/alert';
import { Badge } from '@caterinx/ui/components/ui/badge';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Checkbox } from '@caterinx/ui/components/ui/checkbox';
import { Input } from '@caterinx/ui/components/ui/input';
import { Label } from '@caterinx/ui/components/ui/label';
import { Skeleton } from '@caterinx/ui/components/ui/skeleton';
import { Textarea } from '@caterinx/ui/components/ui/textarea';
import { Wordmark } from '@caterinx/ui/components/ui/wordmark';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { FileField } from '../components/FileField.js';
import { RfqFacts, Row } from '../components/RfqFacts.js';
import { declineRfq, fetchSupplierRfq, formatDate, formatDeadline, formatRemaining, submitOffer } from '../lib/rfq.js';

/**
 * `/ausschreibung/:rfqId` — die Angebotsmaske.
 *
 * ### Die Zahl, die über Vertrauen entscheidet
 *
 * D10 verlangt, dass der Lieferant **vor dem Absenden glasklar** alle drei Zahlen sieht:
 * eigener Preis, Kundenpreis, Provision inklusive USt. Die Anzeige rechnet deshalb live mit —
 * mit **derselben Funktion**, die serverseitig den Angebotsdatensatz erzeugt (`priceOffer` aus
 * `money.ts`). Eine zweite Rechnung im Frontend wäre die eine Stelle, an der der angezeigte
 * Betrag vom fakturierten abweicht, und ein Lieferant, der das erst auf der Rechnung merkt,
 * kommt nicht wieder.
 *
 * ### Zwei Zugangswege, eine Seite
 *
 * Aus der Liste heraus (angemeldet) oder über den Magic-Link aus der Einladungsmail (D7). Der
 * Unterschied ist ein Header. In einem Sechs-Stunden-Fenster ist ein vergessenes Passwort ein
 * verlorenes Angebot — deshalb funktioniert die Maske ohne Anmeldung vollständig, mit einer
 * Ausnahme: Der Menü-Upload braucht ein Konto (ein Upload-Endpunkt für Magic-Link-Inhaber
 * wäre ein anonymer Schreibzugang zu S3).
 *
 * ### Was der Lieferant hier nicht sieht
 *
 * Die Zahl der Mitbewerber, den Kundennamen, die Straße. Nicht, weil die Oberfläche sie
 * ausblendet, sondern weil `RfqSupplierView` sie nicht enthält (§5).
 */

const UNITS: { value: OfferLineItem['unit']; label: string }[] = [
  { value: 'portion', label: 'Portion' },
  { value: 'stueck', label: 'Stück' },
  { value: 'stunde', label: 'Stunde' },
  { value: 'pauschale', label: 'Pauschale' },
  { value: 'liter', label: 'Liter' },
  { value: 'kilogramm', label: 'Kilogramm' },
];

/** Eine Position im Formular. Preise als Text, damit „24,50" während der Eingabe stehen bleibt. */
interface DraftLine {
  id: string;
  label: string;
  description: string;
  quantity: string;
  unit: OfferLineItem['unit'];
  unitPrice: string;
  vatRateBp: number;
}

const EMPTY_INCLUSIONS: OfferInclusions = {
  staff: false,
  tableware: false,
  delivery: false,
  setup: false,
  cleanup: false,
};

function newLine(): DraftLine {
  return {
    id: crypto.randomUUID(),
    label: '',
    description: '',
    quantity: '1',
    unit: 'portion',
    unitPrice: '',
    // 7 % als Vorgabe: Die reine Speisenlieferung ist der häufigere Fall. Wer Personal und
    // Geschirr stellt, stellt bewusst auf 19 % um — raten darf der Code das nicht.
    vatRateBp: VAT_REDUCED_BP,
  };
}

/** `"24,50"` → `2450`. Komma und Punkt gelten beide; alles andere wird verworfen. */
function parseEuroToCents(value: string): number {
  const normalized = value.replace(/\./g, '').replace(',', '.').trim();
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 100);
}

/* --------------------------------------------------------------------------------------------
 * Seite
 * ----------------------------------------------------------------------------------------- */

export function SupplierRfqDetailPage() {
  const { rfqId } = useParams<{ rfqId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? undefined;

  const {
    data,
    isPending,
    error: loadError,
  } = useQuery({
    queryKey: ['supplier-rfq', rfqId, token ?? 'session'],
    enabled: rfqId !== undefined,
    queryFn: () => fetchSupplierRfq(rfqId as string, token),
  });

  if (isPending) {
    return (
      <Shell>
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="mt-4 h-48 w-full" />
      </Shell>
    );
  }

  if (loadError || !data) {
    const notFound = isAxiosError(loadError) && loadError.response?.status === 404;
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertTitle>{notFound ? 'Ausschreibung nicht gefunden' : 'Das hat gerade nicht geklappt'}</AlertTitle>
          <AlertDescription>
            {notFound
              ? 'Der Link ist ungültig, oder Ihr Betrieb ist zu dieser Ausschreibung nicht eingeladen.'
              : 'Bitte laden Sie die Seite in einem Moment neu.'}
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  const { rfq, offer, requesterContact } = data;
  const isOpen = rfq.status === 'broadcast';
  const hasDeclined = rfq.inviteStatus === 'declined';

  return (
    <Shell>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{rfq.brief?.title ?? 'Ausschreibung'}</h1>
          <p className="text-muted-foreground text-sm">
            {formatDate(rfq.eventDate)}, {rfq.eventStartTime} Uhr · {rfq.guestCount} Personen · {rfq.postalCode}{' '}
            {rfq.city}
          </p>
        </div>
        {offer && <Badge>Angebot abgegeben (Fassung {offer.revision})</Badge>}
      </div>

      {rfq.bidsCloseAt && (
        <Alert className="mb-6" variant={isOpen ? 'default' : 'destructive'}>
          <AlertTitle>
            {isOpen ? `Angebotsfrist: ${formatRemaining(rfq.bidsCloseAt)}` : 'Angebotsfrist abgelaufen'}
          </AlertTitle>
          <AlertDescription>
            {isOpen
              ? `Angebote werden angenommen bis ${formatDeadline(rfq.bidsCloseAt)}.`
              : 'Nach Ablauf der Frist können keine Angebote mehr entgegengenommen werden.'}
          </AlertDescription>
        </Alert>
      )}

      {hasDeclined && (
        <Alert className="mb-6">
          <AlertTitle>Sie haben abgesagt</AlertTitle>
          <AlertDescription>
            Wenn Sie es sich anders überlegt haben, melden Sie sich gerne bei uns — wir schalten die Ausschreibung
            wieder für Sie frei.
          </AlertDescription>
        </Alert>
      )}

      {/*
        **Der Zuschlag.** Erst hier gibt es Kundenname, Telefonnummer und die exakte Adresse —
        und nur für den Gewinner (§5, D17). Ob das Feld gefüllt ist, entscheidet
        `assertRfqAccess` auf dem Server; diese Seite prüft nichts nach und zeigt nur an, was
        sie bekommt.
      */}
      {requesterContact && <AwardedContact contact={requesterContact} briefTitle={rfq.brief?.title ?? ''} />}

      {/*
        **Zuschlag ja, Kontaktdaten noch nicht** (D24). Seit dem Zwischenschritt gibt es diesen
        Zustand, und ohne diesen Hinweis wäre er eine leere Stelle: gewonnen, und trotzdem
        keine Telefonnummer. Der Grund steht ausdrücklich da — die Provisionsrechnung. Ihn zu
        verschweigen wäre die unfreundlichste Variante desselben Vorgangs (D10: Konditionen
        gehören nach vorn, nicht auf die Rechnung).
      */}
      {rfq.status === 'awarded' && !rfq.handoverReleased && requesterContact === null && offer?.status === 'won' && (
        <Alert className="mb-6">
          <AlertTitle>Sie haben den Zuschlag — die Kontaktdaten folgen</AlertTitle>
          <AlertDescription>
            Bitte planen Sie den Termin ein. Ansprechpartner, Telefonnummer und die genaue Veranstaltungsadresse geben
            wir frei, sobald Ihre Provisionsrechnung ausgeglichen ist. Sie finden sie unter{' '}
            <Link to="/provisionen" className="underline">
              Provisionen
            </Link>
            ; nach dem Zahlungseingang stehen die Kontaktdaten hier und kommen zusätzlich per E-Mail.
          </AlertDescription>
        </Alert>
      )}

      <RequestDetails rfq={rfq} />

      {isOpen && !hasDeclined && (
        /**
         * `key` setzt das Formular zurück, sobald der Server eine neue Fassung meldet.
         *
         * Der Anfangszustand kommt so aus den Props (`useState(() => …)`) statt aus einem
         * Effekt, der `setState` aufruft — sonst läuft nach jedem erfolgreichen Speichern ein
         * zusätzlicher Renderdurchgang, und der React-Compiler kann die Komponente nicht mehr
         * optimieren.
         */
        <OfferForm key={offer?.updatedAt ?? 'neu'} rfqId={rfqId as string} token={token} rfq={rfq} offer={offer} />
      )}

      {!isOpen && offer && <SubmittedOffer offer={offer} />}

      <p className="text-muted-foreground text-xs">
        <Link to="/ausschreibungen" className="underline">
          Alle Ausschreibungen
        </Link>
      </p>
    </Shell>
  );
}

/* --------------------------------------------------------------------------------------------
 * Die Anfrage
 * ----------------------------------------------------------------------------------------- */

/**
 * Was der Gewinner mit dem Zuschlag bekommt — und niemand sonst, nie früher.
 *
 * Die Karte steht **über** den Anfragedetails: Nach dem Zuschlag ist die nächste Handlung
 * ein Anruf beim Kunden, nicht ein weiterer Blick auf die Gästezahl.
 *
 * Der Hinweis zur Provisionsrechnung gehört dazu. Ein Lieferant, der sie erst auf der
 * Rechnung bemerkt, kommt nicht wieder (D10) — und die Rechnung kommt in wenigen Tagen.
 */
function AwardedContact({ contact, briefTitle }: { contact: AwardedRequesterContact; briefTitle: string }) {
  const address = [contact.street, `${contact.postalCode} ${contact.city}`].filter((line) => line).join(', ');

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Sie haben den Zuschlag</CardTitle>
        <CardDescription>
          {briefTitle !== '' ? `„${briefTitle}" ` : ''}
          Der Auftrag kommt direkt zwischen Ihnen und dem Kunden zustande.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <DetailRow label="Ansprechpartner" value={contact.contactName} />
          {contact.companyName && <DetailRow label="Firma" value={contact.companyName} />}
          <DetailRow label="E-Mail" value={contact.email} />
          {contact.phone && <DetailRow label="Telefon" value={contact.phone} />}
          <DetailRow label="Veranstaltungsort" value={address} />
          {contact.accessNotes && <DetailRow label="Zugang" value={contact.accessNotes} />}
        </dl>
        <p className="text-muted-foreground text-xs">
          Die Provisionsrechnung erhalten Sie in den nächsten Tagen per E-Mail, zahlbar in 10 Tagen per Überweisung.
          Ihre Rechnung an den Kunden stellen Sie über den Kundenpreis — die Provision ist darin bereits enthalten.{' '}
          <Link to="/provisionen" className="underline">
            Zu Ihren Provisionen
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-sm whitespace-pre-wrap">{value}</dd>
    </div>
  );
}

function RequestDetails({ rfq }: { rfq: RfqSupplierView }) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Die Anfrage</CardTitle>
        {rfq.brief && <CardDescription>{rfq.brief.summary}</CardDescription>}
      </CardHeader>
      <CardContent>
        {/*
          D38: Ein Betrieb kalkuliert netto, eine Privatperson sieht brutto und bekommt eine
          Rechnung als Verbraucher. Das gehört vor die Kalkulation, nicht in die Zuschlagsmail.
        */}
        {rfq.requesterType === 'private' && (
          <p className="bg-muted mb-4 rounded-md px-3 py-2 text-sm">
            <strong>Privatperson.</strong> Der Kunde sieht Ihren Kundenpreis einschließlich Umsatzsteuer; Ihre Rechnung
            geht an einen Verbraucher.
          </p>
        )}
        <RfqFacts rfq={rfq} travelSurchargeCents={rfq.travelSurchargeCents} />
      </CardContent>
    </Card>
  );
}

/* --------------------------------------------------------------------------------------------
 * Das Formular
 * ----------------------------------------------------------------------------------------- */

/** Übersetzt ein bestehendes Angebot in den Formularzustand — Überarbeitung ist der Regelfall. */
function linesFromOffer(offer: OfferSupplierView | null): DraftLine[] {
  if (!offer) return [newLine()];

  return offer.lineItems.map((item) => ({
    id: crypto.randomUUID(),
    label: item.label,
    description: item.description,
    quantity: String(item.quantity),
    unit: item.unit,
    unitPrice: (item.unitNetCents / 100).toFixed(2).replace('.', ','),
    vatRateBp: item.vatRateBp,
  }));
}

function OfferForm({
  rfqId,
  token,
  rfq,
  offer,
}: {
  rfqId: string;
  token: string | undefined;
  rfq: RfqSupplierView;
  offer: OfferSupplierView | null;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [lines, setLines] = useState<DraftLine[]>(() => linesFromOffer(offer));
  const [message, setMessage] = useState(offer?.message ?? '');
  const [validUntil, setValidUntil] = useState(offer?.validUntil ? offer.validUntil.slice(0, 10) : '');
  const [inclusions, setInclusions] = useState<OfferInclusions>(offer?.inclusions ?? EMPTY_INCLUSIONS);
  const [attachments, setAttachments] = useState<string[]>(offer?.attachmentS3Keys ?? []);
  const [declineReason, setDeclineReason] = useState('');
  const [showDecline, setShowDecline] = useState(false);

  const commissionRateBp = rfq.commissionRateBp;

  /**
   * Die Live-Rechnung. `priceOffer` ist dieselbe Funktion, die der Server benutzt — deshalb
   * kann die Anzeige nicht vom gespeicherten Betrag abweichen.
   */
  const pricing = useMemo(() => {
    const items = lines
      .map((line) => ({
        netCents: Math.max(0, Number(line.quantity) || 0) * parseEuroToCents(line.unitPrice),
        vatRateBp: line.vatRateBp,
      }))
      .filter((item) => item.netCents > 0);

    if (items.length === 0) return null;
    return priceOffer(items, commissionRateBp);
  }, [lines, commissionRateBp]);

  const submitMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const input: OfferInput = {
        lineItems: lines
          .filter((line) => line.label.trim() !== '' && parseEuroToCents(line.unitPrice) > 0)
          .map((line) => ({
            label: line.label.trim(),
            description: line.description.trim(),
            quantity: Math.max(1, Number(line.quantity) || 1),
            unit: line.unit,
            unitNetCents: parseEuroToCents(line.unitPrice),
            // Der Server rechnet `netCents` selbst aus Menge und Einzelpreis — was hier steht,
            // ist nur der Platzhalter, den das Schema verlangt.
            netCents: 0,
            vatRateBp: line.vatRateBp,
          })),
        message: message.trim(),
        validUntil: validUntil === '' ? null : new Date(`${validUntil}T23:59:59`).toISOString(),
        attachmentS3Keys: attachments,
        inclusions,
      };

      await submitOffer(rfqId, input, token);
    },
    onSuccess: async () => {
      toast({ title: 'Angebot übermittelt', description: 'Der Kunde sieht es ab sofort.' });
      await queryClient.invalidateQueries({ queryKey: ['supplier-rfq', rfqId] });
      await queryClient.invalidateQueries({ queryKey: ['supplier-rfqs'] });
    },
    onError: (error: unknown) => {
      /**
       * Der wichtigste Fehlerfall der ganzen Seite: Die Frist ist zwischen Laden und Absenden
       * abgelaufen. Der Server lehnt das in der `ConditionExpression` ab; hier bekommt es
       * einen Text, der sagt, was passiert ist — nicht „Fehler 409".
       */
      const code = isAxiosError(error) ? (error.response?.data as { code?: string } | undefined)?.code : undefined;
      toast({
        variant: 'destructive',
        title: code === 'DEADLINE_PASSED' ? 'Die Frist ist abgelaufen' : 'Angebot konnte nicht gespeichert werden',
        description:
          code === 'DEADLINE_PASSED'
            ? 'Angebote können nach Ablauf der Frist nicht mehr entgegengenommen werden. Melden Sie sich gerne bei uns.'
            : 'Bitte prüfen Sie Ihre Eingaben und versuchen Sie es erneut.',
      });
    },
  });

  const declineMutation = useMutation({
    mutationFn: () => declineRfq(rfqId, declineReason, token),
    onSuccess: async () => {
      toast({ title: 'Absage vermerkt', description: 'Danke — das hilft uns beim nächsten Mal.' });
      setShowDecline(false);
      await queryClient.invalidateQueries({ queryKey: ['supplier-rfq', rfqId] });
      await queryClient.invalidateQueries({ queryKey: ['supplier-rfqs'] });
    },
  });

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">{offer ? 'Angebot überarbeiten' : 'Ihr Angebot'}</CardTitle>
        <CardDescription>
          Erfassen Sie die Positionen mit Netto-Einzelpreisen. Der USt-Satz hängt an der Position: 7 % für die reine
          Speisenlieferung, 19 % mit Personal oder Geschirr.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {lines.map((line, index) => (
            <LineEditor
              key={line.id}
              line={line}
              index={index}
              canRemove={lines.length > 1}
              onChange={(patch) =>
                setLines((current) => current.map((entry) => (entry.id === line.id ? { ...entry, ...patch } : entry)))
              }
              onRemove={() => setLines((current) => current.filter((entry) => entry.id !== line.id))}
            />
          ))}

          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => setLines((current) => [...current, newLine()])}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Position hinzufügen
          </Button>
        </div>

        <PriceSummary pricing={pricing} commissionRateBp={commissionRateBp} guestCount={rfq.guestCount} />

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">Im Preis enthalten</legend>
          <p className="text-muted-foreground text-sm">
            Diese Häkchen entscheiden darüber, ob der Kunde Ihr Angebot mit anderen vergleichen kann. Zwei Angebote über
            denselben Betrag sind unvergleichbar, solange nicht dasteht, was drinsteckt.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {(Object.keys(OFFER_INCLUSION_LABELS) as (keyof OfferInclusions)[]).map((key) => (
              <label key={key} className="flex min-h-11 items-center gap-2 text-sm">
                <Checkbox
                  /* eslint-disable-next-line security/detect-object-injection -- Schlüssel aus einer festen Tabelle */
                  checked={inclusions[key]}
                  onCheckedChange={(checked) => setInclusions((current) => ({ ...current, [key]: checked === true }))}
                />
                {/* eslint-disable-next-line security/detect-object-injection -- dito */}
                {OFFER_INCLUSION_LABELS[key]}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="space-y-2">
          <Label htmlFor="offer-message">Nachricht an den Kunden</Label>
          <Textarea
            id="offer-message"
            rows={4}
            maxLength={4000}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Ablauf, Besonderheiten, was im Preis enthalten ist."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="offer-valid-until">Angebot gültig bis (optional)</Label>
          <Input
            id="offer-valid-until"
            type="date"
            className="max-w-xs"
            value={validUntil}
            onChange={(event) => setValidUntil(event.target.value)}
          />
        </div>

        {token === undefined ? (
          <FileField
            kind="offer_attachment"
            label="Menüvorschlag (optional)"
            hint="PDF oder Foto, bis zu fünf Dateien."
            accept="application/pdf,image/jpeg,image/png,image/webp"
            currentUrl={null}
            onUploaded={(s3Key) => setAttachments((current) => [...current, s3Key].slice(0, 5))}
          />
        ) : (
          <p className="text-muted-foreground text-sm">
            Einen Menüvorschlag können Sie anhängen, wenn Sie sich anmelden. Ihr Angebot können Sie auch ohne Anmeldung
            abgeben.
          </p>
        )}
        {attachments.length > 0 && (
          <p className="text-muted-foreground text-sm">
            {attachments.length} {attachments.length === 1 ? 'Anhang' : 'Anhänge'} angefügt.
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          <Button
            className="min-h-11"
            disabled={pricing === null || submitMutation.isPending}
            onClick={() => submitMutation.mutate()}
          >
            {submitMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {offer ? 'Angebot aktualisieren' : 'Angebot abgeben'}
          </Button>
          <Button variant="outline" className="min-h-11" onClick={() => setShowDecline((current) => !current)}>
            Absagen
          </Button>
        </div>

        {showDecline && (
          <div className="space-y-3 rounded-lg border p-4">
            <Label htmlFor="decline-reason">Warum passt die Anfrage nicht? (optional)</Label>
            <p className="text-muted-foreground text-sm">
              Auch Absagen sind wertvoll — sie sagen uns, ob wir Sie richtig ausgewählt haben.
            </p>
            <Textarea
              id="decline-reason"
              rows={2}
              maxLength={500}
              value={declineReason}
              onChange={(event) => setDeclineReason(event.target.value)}
              placeholder="Ausgebucht, zu weit entfernt, zu kurzfristig …"
            />
            <Button
              variant="destructive"
              className="min-h-11"
              disabled={declineMutation.isPending}
              onClick={() => declineMutation.mutate()}
            >
              {declineMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              Absage senden
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* --------------------------------------------------------------------------------------------
 * Positionen
 * ----------------------------------------------------------------------------------------- */

function LineEditor({
  line,
  index,
  canRemove,
  onChange,
  onRemove,
}: {
  line: DraftLine;
  index: number;
  canRemove: boolean;
  onChange: (patch: Partial<DraftLine>) => void;
  onRemove: () => void;
}) {
  const lineTotal = Math.max(0, Number(line.quantity) || 0) * parseEuroToCents(line.unitPrice);

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          Position {index + 1}
        </span>
        {canRemove && (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove} aria-label="Position entfernen">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`label-${line.id}`}>Bezeichnung</Label>
        <Input
          id={`label-${line.id}`}
          value={line.label}
          maxLength={200}
          onChange={(event) => onChange({ label: event.target.value })}
          placeholder="Fingerfood-Buffet, Servicekraft, Anlieferung …"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor={`qty-${line.id}`}>Menge</Label>
          <Input
            id={`qty-${line.id}`}
            type="number"
            inputMode="numeric"
            min={1}
            value={line.quantity}
            onChange={(event) => onChange({ quantity: event.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`unit-${line.id}`}>Einheit</Label>
          <select
            id={`unit-${line.id}`}
            className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
            value={line.unit}
            onChange={(event) => onChange({ unit: event.target.value as OfferLineItem['unit'] })}
          >
            {UNITS.map((unit) => (
              <option key={unit.value} value={unit.value}>
                {unit.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`price-${line.id}`}>Einzelpreis netto</Label>
          <Input
            id={`price-${line.id}`}
            inputMode="decimal"
            value={line.unitPrice}
            onChange={(event) => onChange({ unitPrice: event.target.value })}
            placeholder="24,50"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <fieldset>
          <legend className="mb-2 text-sm font-medium">USt-Satz</legend>
          <div className="flex gap-2">
            {[VAT_REDUCED_BP, VAT_STANDARD_BP].map((rate) => (
              <Button
                key={rate}
                type="button"
                variant={line.vatRateBp === rate ? 'default' : 'outline'}
                size="sm"
                className="min-h-9"
                onClick={() => onChange({ vatRateBp: rate })}
              >
                {formatVatRate(rate)}
              </Button>
            ))}
          </div>
        </fieldset>

        <p className="text-sm">
          <span className="text-muted-foreground">Positionssumme: </span>
          <strong>{formatEur(lineTotal)}</strong>
        </p>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------------------------
 * Die drei Zahlen aus D10
 * ----------------------------------------------------------------------------------------- */

function PriceSummary({
  pricing,
  commissionRateBp,
  guestCount,
}: {
  pricing: ReturnType<typeof priceOffer> | null;
  commissionRateBp: number;
  guestCount: number;
}) {
  if (!pricing) {
    return (
      <div className="text-muted-foreground rounded-lg border border-dashed p-4 text-sm">
        Sobald Sie eine Position mit Preis erfasst haben, sehen Sie hier Ihren Preis, den Kundenpreis und Ihre
        Provision.
      </div>
    );
  }

  return (
    <div className="bg-muted/50 space-y-3 rounded-lg border p-4">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm">Ihr Preis (netto)</span>
        <strong className="text-lg">{formatEur(pricing.supplier.netCents)}</strong>
      </div>

      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm">
          Vermittlungsentgelt caterinx ({formatVatRate(commissionRateBp)})
          <br />
          <span className="text-muted-foreground text-xs">
            wird Ihnen nach Zuschlag berechnet: {formatEur(pricing.commission.netCents)} + USt ={' '}
            {formatEur(pricing.commission.grossCents)}
          </span>
        </span>
        <span className="text-muted-foreground">+ {formatEur(pricing.commission.netCents)}</span>
      </div>

      <div className="flex items-baseline justify-between gap-3 border-t pt-3">
        <span className="text-sm font-medium">
          Kundenpreis (netto)
          <br />
          <span className="text-muted-foreground text-xs">
            diesen Betrag fakturieren Sie dem Kunden · {formatEur(Math.round(pricing.customer.netCents / guestCount))}{' '}
            pro Person
          </span>
        </span>
        <strong className="text-lg">{formatEur(pricing.customer.netCents)}</strong>
      </div>

      {pricing.supplier.groups.length > 1 && (
        <p className="text-muted-foreground border-t pt-3 text-xs">
          Enthält {pricing.supplier.groups.map((group) => formatVatRate(group.vatRateBp)).join(' und ')} USt — die
          Aufschlüsselung steht auf Ihrer Rechnung an den Kunden.
        </p>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------------------------
 * Nach Fristablauf
 * ----------------------------------------------------------------------------------------- */

function SubmittedOffer({ offer }: { offer: OfferSupplierView }) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">Ihr abgegebenes Angebot</CardTitle>
        <CardDescription>
          Die Frist ist abgelaufen. Der Kunde vergleicht jetzt; wir melden uns, sobald er entschieden hat.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <Row label="Ihr Preis (netto)" value={formatEur(offer.supplierTotal.netCents)} />
          <Row label="Kundenpreis (netto)" value={formatEur(offer.customerTotal.netCents)} />
          <Row
            label="Ihre Provision"
            value={`${formatEur(offer.commission.netCents)} + USt = ${formatEur(offer.commission.grossCents)}`}
          />
          <Row label="Fassung" value={String(offer.revision)} />
        </dl>
      </CardContent>
    </Card>
  );
}

/* --------------------------------------------------------------------------------------------
 * Rahmen
 * ----------------------------------------------------------------------------------------- */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto w-full max-w-3xl p-4 sm:p-6">
      <Wordmark className="mb-8 w-40" />
      {children}
    </main>
  );
}
